import { useContext, useEffect, useState } from 'react';
import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import Rolling from '@assets/gif/rolling.gif';
import ImagePlaceholderPNG from '@assets/png/manufacturer-placeholder.png';
import ProductPlaceholderPNG from '@assets/png/product.png';

import { IProduct } from '@models/Product.model';
import { IManufacturer } from '@models/Manufacturer.model';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { indexManufacturersPagination } from '@services/queries/manufacturers';
import { getProducts } from '@services/queries/products';
import { useInView } from 'react-intersection-observer';
import { Shimmer } from '@components/index';
import {
  createEquipments,
  patchEquipmentsMatch,
} from '@services/queries/equipments';
import {
  addKnownEquipments,
  EqRet,
} from '@store/redux/features/inventory/knownData';
import { useDispatch } from 'react-redux';
import { IEquipment, IEquipmentWithoutProduct } from '@models/Equipment.model';
import { EQUIPMENTS_PICKER_SEND_TYPE } from '@store/machines/equipmentsPicker';
import { ICreateEquipments } from '@services/queries/equipments/createEquipments/createEquipments';
import { createAsset } from '@services/queries/onboarding/createAsset';
import { NavigationContext } from '@layout/navigation/navigation';
import {
  Body,
  ButtonMain,
  ButtonSimple,
  CloseButton,
  Container,
  Description,
  Header,
  HeaderBar,
  Overlay,
  Buttons,
  Inputs,
  InputContainer,
  Input,
  IconLeft,
  GlassIcon,
  CleanSearch,
  CleanButton,
  Skipped,
  ResultsText,
  ScrollView,
  LoadMore,
  LoadingText,
  LoadingImg,
  ManufacturerContainer,
  ProductContainer,
  LoadMoreContainer,
  Radio,
  ItemName,
  ItemImage,
  ProductValue,
  FullDetailsButton,
  InternalContainer,
  InternalText,
  LoadingSearch,
  ProductTitles,
  TitleTable,
  TitleTableSpace,
  ItemEmpty,
  AssetContainer,
  AssetContent,
  AssetTitle,
  AssetIcon,
  AssetField,
  AssetEmpty,
} from './ManualAdd.styles';
import {
  QuantityCounter,
  ProductDialog,
  ProductDetail,
  InternalProductDialog,
  MatchChanged,
} from './components';
import { toMap } from '../OnBoardingModal/OnBoardingModal.utils';

interface OwnProps {
  open: boolean;
  onClose: () => void;
  send?: EQUIPMENTS_PICKER_SEND_TYPE;
  oldAsset?: IEquipment | IEquipmentWithoutProduct;
  isOnboarding?: boolean;
}

export const ManualAdd = ({
  open,
  onClose,
  send,
  oldAsset,
  isOnboarding,
}: OwnProps) => {
  const portalDiv = document.getElementById('portal');

  const { studioId, studioName, userEmail, userId, userName, userPicture } =
    useContext(NavigationContext);

  const queryClient = useQueryClient();

  const manufacturersInView = useInView();
  const productsInView = useInView();
  const dispatch = useDispatch();

  const [asset] = useState<IEquipment | IEquipmentWithoutProduct | undefined>(
    oldAsset,
  );

  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const [productDetailOpen, setProductDetailOpen] = useState<boolean>(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState<boolean>(false);
  const [successMatchOpen, setSuccessMatchOpen] = useState<boolean>(false);
  const [count, setCount] = useState<number>(1);
  const [product, setProduct] = useState<IProduct | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [updatedAsset, setUpdatedAsset] = useState<IEquipment | null>(null);
  const [manufacturer, setManufacturer] = useState<IManufacturer | null>(() => {
    if (asset?.product?.manufacturer) {
      if (asset?.product?.manufacturer.status !== 'internal') {
        return asset?.product?.manufacturer;
      }
    }

    return null;
  });
  const [selectedManufacturer, setSelectedManufacturer] =
    useState<IManufacturer | null>(null);
  const [skipManufacturer, setSkipManufacturer] = useState<boolean>(false);
  const [searchManufacturer, setSearchManufacturer] = useState<string>(
    asset?.product?.manufacturer.name || '',
  );
  const [searchManufacturerEnter, setSearchManufacturerEnter] =
    useState<string>(asset?.product?.manufacturer.name || '');
  const [searchProduct, setSearchProduct] = useState<string>(() => {
    if (asset?.product?.manufacturer) {
      if (asset?.product?.manufacturer.status !== 'internal') {
        return asset.product.name;
      }
    }

    return '';
  });
  const [searchProductEnter, setSearchProductEnter] = useState<string>(() => {
    if (asset?.product?.manufacturer) {
      if (asset?.product?.manufacturer.status !== 'internal') {
        return asset.product.name;
      }
    }

    return '';
  });
  const [equipments, setEquipments] = useState<Array<IEquipment>>([]);
  const [isInternal, setIsInternal] = useState<boolean>(false);

  const manufacturersQuery = useInfiniteQuery(
    ['manualAdd', 'manufacturers', searchManufacturerEnter],
    async ({ pageParam = 1 }) => {
      return indexManufacturersPagination({
        pagination: {
          limit: 20,
          page: pageParam,
          search: searchManufacturerEnter,
        },
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      enabled: !!searchManufacturerEnter,
    },
  );

  const productsQuery = useInfiniteQuery(
    ['manualAdd', 'products', manufacturer?.name, searchProductEnter],
    async ({ pageParam = 1 }) => {
      return getProducts({
        pagination: {
          limit: 20,
          page: pageParam,
          search: searchProductEnter,
        },
        filter: manufacturer
          ? {
              manufacturerId: manufacturer.id,
              sort: 'product.name',
              direction: 'ASC',
            }
          : { sort: 'product.name', direction: 'ASC' },
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
    },
  );

  const hasMoreManufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages[manufacturersQuery.data.pages.length - 1]
        .hasMore
    : false;

  const hasMoreProducts = productsQuery.data
    ? productsQuery.data.pages[productsQuery.data.pages.length - 1].hasMore
    : false;

  const manufacturers = manufacturersQuery.data
    ? manufacturersQuery.data.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const products = productsQuery.data
    ? productsQuery.data.pages
        .map(pageData => pageData.results)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  const mutationCreateEquipments = useMutation(
    async (data: ICreateEquipments) => {
      if (isOnboarding) {
        return createAsset({
          amountProducts: data.amountProducts,
          studioId,
          studioName,
          user: {
            triggeredByAvatar: userPicture,
            triggeredByEmail: userEmail,
            triggeredById: userId,
            triggeredBy: userName,
          },
        });
      }

      return createEquipments(data);
    },
    {
      onSuccess: newEquipments => {
        setSuccessDialogOpen(true);

        if (newEquipments) {
          dispatch(addKnownEquipments(newEquipments as EqRet[]));
          setEquipments(newEquipments);
        } else {
          onClose();
        }

        queryClient.invalidateQueries(['inventory', 'equipments', 'pages']);
      },
    },
  );

  const mutationMatchEquipment = useMutation(patchEquipmentsMatch, {
    onSuccess: updatedEquipment => {
      setSuccessMatchOpen(true);
      setUpdatedAsset(updatedEquipment);
      queryClient.invalidateQueries(['equipments']);
    },
  });

  const confirmLoading =
    mutationCreateEquipments.isLoading || mutationMatchEquipment.isLoading;

  useEffect(() => {
    if (manufacturersInView.inView && hasMoreManufacturers)
      manufacturersQuery.fetchNextPage();
  }, [manufacturersInView.inView, hasMoreManufacturers]);

  useEffect(() => {
    if (productsInView.inView && hasMoreProducts) productsQuery.fetchNextPage();
  }, [productsInView.inView, hasMoreProducts]);

  const resultsText = () => {
    if (!manufacturer && !skipManufacturer) {
      if (searchManufacturerEnter) {
        if (manufacturersQuery.isLoading) {
          return 'Loading results';
        }

        if (manufacturers.length === 0) {
          return 'No results found';
        }

        return `${manufacturersQuery.data?.pages[0].total} Results found`;
      }

      return 'Search for a Manufacturer to see results';
    }

    if (productsQuery.isLoading) {
      return 'Loading results';
    }

    if (products.length === 0) {
      return 'No results found';
    }

    return `${productsQuery.data?.pages[0].total} Results found`;
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container isLarger={!!asset}>
            <HeaderBar>
              <Header>{asset ? 'Change Match' : 'Add a New Asset'}</Header>
              <CloseButton onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </CloseButton>
            </HeaderBar>
            <Body>
              <Description>
                Search through Beam’s verified product database of over 300k
                products from thousands of vendors. If this is a custom product
                use the skip button below to add an internal asset to your
                inventory.
              </Description>
              {asset && (
                <AssetContainer>
                  <AssetContent>
                    <AssetTitle>Product Name</AssetTitle>
                    {asset.product?.name ? (
                      <AssetField>{`"${asset.product?.name}"`}</AssetField>
                    ) : (
                      <AssetEmpty>Empty</AssetEmpty>
                    )}
                    {asset.product?.status === 'internal' && (
                      <AssetIcon>
                        <BlueIcon icon="error" color="#8F95B2" size={12} />
                      </AssetIcon>
                    )}
                  </AssetContent>
                  <AssetContent>
                    <AssetTitle>Model #</AssetTitle>
                    {asset.product?.model ? (
                      <AssetField>{`"${asset.product?.model}"`}</AssetField>
                    ) : (
                      <AssetEmpty>Empty</AssetEmpty>
                    )}
                    {asset.product?.status === 'internal' && (
                      <AssetIcon>
                        <BlueIcon icon="error" color="#8F95B2" size={12} />
                      </AssetIcon>
                    )}
                  </AssetContent>
                  <AssetContent>
                    <AssetTitle>Manufacturer</AssetTitle>
                    {asset.product?.manufacturer?.name ? (
                      <AssetField>{`${asset.product?.manufacturer?.name}`}</AssetField>
                    ) : (
                      <AssetEmpty>Empty</AssetEmpty>
                    )}
                    {asset.product?.manufacturer.status === 'internal' && (
                      <AssetIcon>
                        <BlueIcon icon="error" color="#8F95B2" size={12} />
                      </AssetIcon>
                    )}
                  </AssetContent>
                </AssetContainer>
              )}

              <Inputs>
                <InputContainer>
                  {!!manufacturer && (
                    <IconLeft
                      src={manufacturer.imageUrl || ImagePlaceholderPNG}
                      brokenSrc={ImagePlaceholderPNG}
                    />
                  )}
                  {!manufacturer && (
                    <GlassIcon>
                      <BlueIcon icon="search" color="#8F95B2" size={12} />
                    </GlassIcon>
                  )}
                  <Input
                    placeholder="Search Manufacturer"
                    disabled={skipManufacturer || confirmLoading}
                    isSearch={!manufacturer}
                    value={searchManufacturer}
                    onChange={e => setSearchManufacturer(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setSearchManufacturerEnter(searchManufacturer);
                      }

                      setManufacturer(null);
                      setSelectedManufacturer(null);
                      setSearchProduct('');
                      setSearchProductEnter('');
                    }}
                  />
                  {skipManufacturer && <Skipped>Skipped</Skipped>}
                  {searchManufacturer &&
                    !confirmLoading &&
                    !manufacturersQuery.isLoading && (
                      <CleanSearch>
                        <CleanButton
                          onClick={() => {
                            setSearchManufacturer('');
                            setSearchManufacturerEnter('');
                            setSearchProduct('');
                            setSearchProductEnter('');
                            setManufacturer(null);
                            setSelectedManufacturer(null);
                            setCount(1);
                            setIsInternal(false);
                          }}
                        >
                          <BlueIcon icon="cross" color="#8F95B2" size={12} />
                        </CleanButton>
                      </CleanSearch>
                    )}
                  {!manufacturer &&
                    !skipManufacturer &&
                    manufacturersQuery.isLoading && (
                      <LoadingSearch src={Rolling} alt="loading" />
                    )}
                </InputContainer>
                <InputContainer>
                  <GlassIcon>
                    <BlueIcon icon="search" color="#8F95B2" size={12} />
                  </GlassIcon>
                  <Input
                    placeholder="Type a Product Name or a Serial #"
                    disabled={
                      (!manufacturer && !skipManufacturer) || confirmLoading
                    }
                    isSearch
                    value={searchProduct}
                    onChange={e => setSearchProduct(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setSearchProductEnter(searchProduct);
                      }
                    }}
                  />
                  {searchProduct &&
                    !confirmLoading &&
                    !productsQuery.isLoading && (
                      <CleanSearch>
                        <CleanButton
                          onClick={() => {
                            setSearchProduct('');
                            setSearchProductEnter('');
                            setProduct(null);
                            setIsInternal(false);
                            setCount(1);
                          }}
                        >
                          <BlueIcon icon="cross" color="#8F95B2" size={12} />
                        </CleanButton>
                      </CleanSearch>
                    )}
                  {(!!manufacturer || skipManufacturer) &&
                    productsQuery.isLoading && (
                      <LoadingSearch src={Rolling} alt="loading" />
                    )}
                </InputContainer>
              </Inputs>
              <ResultsText>{resultsText()}</ResultsText>
              {!manufacturer && !skipManufacturer && searchManufacturerEnter && (
                <ProductTitles>
                  <TitleTable>
                    <TitleTableSpace /> Manufacturer Name
                  </TitleTable>
                </ProductTitles>
              )}
              {(!!manufacturer || skipManufacturer) && searchProductEnter && (
                <ProductTitles>
                  <TitleTable>
                    <TitleTableSpace /> Product Name
                  </TitleTable>
                  <TitleTable>Model #</TitleTable>
                  <TitleTable>Manufacturer</TitleTable>
                  <TitleTable />
                </ProductTitles>
              )}
              <ScrollView>
                {(manufacturersQuery.isLoading ||
                  (productsQuery.isLoading &&
                    (manufacturer || skipManufacturer))) && (
                  <>
                    <Shimmer
                      style={{ height: 44, width: '100%', borderRadius: 4 }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                    <Shimmer
                      style={{
                        height: 44,
                        width: '100%',
                        borderRadius: 4,
                        marginTop: 4,
                      }}
                      primary="#f1f1f1"
                      secondary="#ececec"
                    />
                  </>
                )}
                {!manufacturer && !skipManufacturer && searchManufacturerEnter && (
                  <>
                    {manufacturers.map(item => (
                      <ManufacturerContainer
                        key={item.id}
                        onClick={() => {
                          setSelectedManufacturer(item);
                          setIsInternal(false);
                        }}
                      >
                        <Radio active={selectedManufacturer?.id === item.id} />
                        <ItemImage
                          src={item.imageUrl || ImagePlaceholderPNG}
                          brokenSrc={ImagePlaceholderPNG}
                        />
                        {item.name ? (
                          <ItemName>{item.name}</ItemName>
                        ) : (
                          <ItemEmpty>Empty</ItemEmpty>
                        )}
                        {item.status === 'internal' && (
                          <InternalContainer>
                            <BlueIcon icon="issue" color="#8F95B2" size={12} />
                            <InternalText>Internal</InternalText>
                          </InternalContainer>
                        )}
                      </ManufacturerContainer>
                    ))}
                    <LoadMoreContainer ref={manufacturersInView.ref}>
                      {hasMoreManufacturers ? (
                        <LoadMore>
                          <LoadingText>Loading more</LoadingText>
                          <LoadingImg src={Rolling} alt="loading" />
                        </LoadMore>
                      ) : (
                        <div />
                      )}
                    </LoadMoreContainer>
                  </>
                )}
                {(!!manufacturer || skipManufacturer) &&
                  !productsQuery.isLoading && (
                    <>
                      {products.map(item => (
                        <ProductContainer
                          key={item.id}
                          onClick={() => {
                            if (!confirmLoading) {
                              setProduct(item);
                              setIsInternal(false);
                            }
                          }}
                        >
                          <ProductValue>
                            <Radio active={product?.id === item.id} />
                            <ItemImage
                              src={item.imageUrl || ProductPlaceholderPNG}
                              brokenSrc={ProductPlaceholderPNG}
                            />
                            {item.name ? (
                              <ItemName>{item.name}</ItemName>
                            ) : (
                              <ItemEmpty>Empty</ItemEmpty>
                            )}
                          </ProductValue>
                          <ProductValue>
                            {item.model ? (
                              <ItemName>{item.model}</ItemName>
                            ) : (
                              <ItemEmpty>Empty</ItemEmpty>
                            )}
                          </ProductValue>
                          <ProductValue>
                            <ItemName>{item.manufacturer?.name}</ItemName>
                          </ProductValue>
                          <ProductValue>
                            {item.status === 'internal' ? (
                              <InternalContainer>
                                <BlueIcon
                                  icon="issue"
                                  color="#8F95B2"
                                  size={12}
                                />
                                <InternalText>Internal</InternalText>
                              </InternalContainer>
                            ) : (
                              <FullDetailsButton
                                onClick={e => {
                                  e.stopPropagation();
                                  setProductDetailOpen(true);
                                  setSelectedProduct(item);
                                }}
                              >
                                Full Details
                              </FullDetailsButton>
                            )}
                          </ProductValue>
                        </ProductContainer>
                      ))}
                      <LoadMoreContainer ref={productsInView.ref}>
                        {hasMoreProducts ? (
                          <LoadMore>
                            <LoadingText>Loading more</LoadingText>
                            <LoadingImg src={Rolling} alt="loading" />
                          </LoadMore>
                        ) : (
                          <div />
                        )}
                      </LoadMoreContainer>
                    </>
                  )}
              </ScrollView>
              {((searchManufacturerEnter && !manufacturer) ||
                searchProductEnter) && (
                <ManufacturerContainer
                  onClick={() => {
                    setSelectedManufacturer(null);
                    setProduct(null);
                    setIsInternal(true);
                  }}
                >
                  <Radio active={isInternal} />
                  <ItemName>
                    Add{' '}
                    {`"${
                      manufacturer || skipManufacturer
                        ? searchProductEnter
                        : searchManufacturerEnter
                    }"`}{' '}
                    as Internal{' '}
                    {manufacturer || skipManufacturer
                      ? 'Product'
                      : 'Manufacturer'}
                  </ItemName>
                </ManufacturerContainer>
              )}
            </Body>
            <Buttons>
              {!manufacturer && !skipManufacturer && (
                <ButtonSimple
                  onClick={() => {
                    setSkipManufacturer(true);
                    setManufacturer(null);
                    setSearchManufacturer('');
                    setSearchManufacturerEnter('');
                    setIsInternal(false);
                  }}
                >
                  Skip Manufacturer
                </ButtonSimple>
              )}
              {(manufacturer || skipManufacturer) && (
                <ButtonSimple
                  onClick={() => {
                    if (!confirmLoading) {
                      setManufacturer(null);
                      setProduct(null);
                      setSkipManufacturer(false);
                      setSearchProduct('');
                      setSearchProductEnter('');
                      setCount(1);
                      setIsInternal(false);
                    }
                  }}
                >
                  Back
                </ButtonSimple>
              )}
              {!asset && (manufacturer || skipManufacturer) && (
                <QuantityCounter
                  count={count}
                  setCount={setCount}
                  disabled={(!product && !isInternal) || confirmLoading}
                />
              )}
              {!manufacturer && !skipManufacturer && (
                <ButtonMain
                  disabled={!selectedManufacturer && !isInternal}
                  onClick={() => {
                    if (isInternal) {
                      setInternalOpen(true);
                    } else {
                      setManufacturer(selectedManufacturer);
                      if (selectedManufacturer) {
                        setSearchManufacturer(selectedManufacturer.name);
                      }
                    }
                  }}
                >
                  Continue
                </ButtonMain>
              )}
              {(manufacturer || skipManufacturer) && (
                <ButtonMain
                  disabled={(!product || confirmLoading) && !isInternal}
                  onClick={() => {
                    if (isInternal) {
                      setInternalOpen(true);
                    } else if (product) {
                      if (asset) {
                        mutationMatchEquipment.mutate({
                          equipmentId: asset.id,
                          productId: product.id,
                          manufacturerId: product.manufacturerId,
                        });
                      } else {
                        mutationCreateEquipments.mutate({
                          amountProducts: [
                            {
                              amount: count,
                              manufacturerId: product.manufacturerId,
                              productId: product.id,
                            },
                          ],
                        });
                      }
                    }
                  }}
                >
                  {asset ? 'Change Match' : 'Add Asset(s)'}
                </ButtonMain>
              )}
              {successDialogOpen && equipments.length > 0 && (
                <ProductDialog
                  open={successDialogOpen}
                  onClose={() => {
                    setSuccessDialogOpen(false);
                    onClose();
                  }}
                  amount={count}
                  equipments={equipments}
                  onReset={() => {
                    setSuccessDialogOpen(false);
                    setManufacturer(null);
                    setProduct(null);
                    setSkipManufacturer(false);
                    setSearchManufacturer('');
                    setSearchManufacturerEnter('');
                    setSearchProduct('');
                    setSearchProductEnter('');
                    setCount(1);
                    setInternalOpen(false);
                  }}
                  onDetails={() => {
                    onClose();
                    setInternalOpen(false);
                    if (send) {
                      send({
                        type: 'LAUNCH',
                        data: toMap(equipments as EqRet[]),
                        modalName: 'MASS_EDIT',
                      });
                    }
                  }}
                />
              )}
              {successMatchOpen && asset && updatedAsset && (
                <MatchChanged
                  open={successMatchOpen}
                  onClose={() => {
                    setSuccessMatchOpen(false);
                    onClose();
                  }}
                  asset={asset}
                  updatedAsset={updatedAsset}
                />
              )}
              {productDetailOpen && selectedProduct && (
                <ProductDetail
                  open={productDetailOpen}
                  onClose={() => setProductDetailOpen(false)}
                  product={selectedProduct}
                  onSelectProduct={() => {
                    setProduct(selectedProduct);
                    setProductDetailOpen(false);
                  }}
                />
              )}
              {internalOpen && (
                <InternalProductDialog
                  open={internalOpen}
                  onClose={() => setInternalOpen(false)}
                  productName={searchProductEnter}
                  count={count}
                  setCount={setCount}
                  startManufacturer={manufacturer}
                  startSearchManufacturer={searchManufacturerEnter}
                  mutationCreateEquipments={mutationCreateEquipments}
                  mutationMatchEquipment={mutationMatchEquipment}
                  showCounter={!asset}
                  asset={asset}
                />
              )}
            </Buttons>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
